<script setup lang="ts">
import type { PortableTextBlock } from 'sanity';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@pkg/ui/components/ui/carousel'; import { watchOnce } from '@vueuse/core';
import { CarouselPrevious, CarouselNext } from '@pkg/ui/components/ui/carousel';
import PortableEditor from '@pkg/portable-text-editor/web/PortableEditor.vue';
import { logger } from '@pkg/logger';
import { ampli } from '@pkg/analytics-helper/src/ampli';

defineProps<{
  steps: Array<{
    icon: string;
    title: string;
    content: Array<PortableTextBlock>;
  }>;
}>();

const api = ref<CarouselApi>();
const currentStep = ref(0);

const setApi = (val: CarouselApi) => {
  api.value = val;
};

watchOnce(api, (api) => {
  if (!api) {
    return;
  }
  currentStep.value = api.selectedScrollSnap();

  api.on('select', () => {
    const newStep = api.selectedScrollSnap();
    if (newStep !== currentStep.value) {
      try {
        ampli.howTgtgWorksWidget({
          how_tgtg_works_page_number: `${currentStep.value}`,
        });
      } catch (error) {
        logger.error('[Nudge] Amplitude error:', error);
      }
    }
    currentStep.value = newStep;
  });
});

</script>

<template>
  <Carousel class="c-nudge" @init-api="setApi">
    <CarouselContent v-if="steps">
      <CarouselItem v-for="(step, index) in steps" :key="index" class="c-nudge__content">
        <CarouselPrevious class="c-nudge__cta">
          <ChevronLeftIcon />
        </CarouselPrevious>
        <div class="c-nudge__text__wrapper">
          <p class="c-nudge__text__title">
            {{ step?.title }}
          </p>
          <PortableEditor :content="step?.content" class="c-nudge__text" />
        </div>
        <CmsIcon :name="step?.icon" class="text-56 text-green-30" />
        <CarouselNext class="c-nudge__cta">
          <ChevronRightIcon :initial-count="steps?.length" />
        </CarouselNext>
      </CarouselItem>
    </CarouselContent>
  </Carousel>
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

.c-nudge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: theme('spacing.12') theme('spacing.16');
  border-radius: theme('spacing.18');
  color: theme('colors.white');
  background: radial-gradient(129.47% 178.23% at 0% 0%, #007573 0%, #00615F 48.56%, #004746 100%),
              linear-gradient(0deg, #ECEFEE, #ECEFEE);

  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__cta {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    padding: theme('spacing.4') theme('spacing.4');
    border: 1px solid color-mix(in srgb, theme('colors.neutral.30') 33%, transparent);
  }

  &__text {
    @mixin body;

    @screen tablet {
      @mixin body-xsmall;
    }

    &__wrapper {
    text-align: left;
    display: flex;
    flex-direction: column;
    padding: theme('spacing.0') theme('spacing.0') theme('spacing.0') theme('spacing.16');
    width: 100%;

    @screen tablet-wide {
      padding: theme('spacing.0') theme('spacing.8') theme('spacing.0') theme('spacing.16');
    }
  }

    &__title {
      @mixin body-large;
      font-weight: bold;

      @screen tablet {
        @mixin body-small;
        font-weight: bold;
      }
    }
  }
}
</style>
